import { LitElement, html } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../../shared/services/debug.service.js';
import { ApplicationService } from '../../../../services/application.service.js';
import { style } from './name.style.js';
import { globalStyle } from '../../../../global-styles.style.js';
import { stratum } from '../../../../../constants/stratum.js';

class RefiPersonalNameComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepName');
    this.values = {};
    this.applicantType = 'primaryApplicant';
    this.coAppPrefix = '';
    this.firstNameId = '';
    this.lastNameId = '';
    this.firstNameFieldId = '';
    this.lastNameFieldId = '';
    this.compact = false;
    this.iconicV2 = ApplicationService.isIconicV2;
  }

  static properties = {
    applicantType: { type: String, attribute: 'applicant-type', reflect: true },
    applicant: { type: Object, reflect: true },
    compact: { type: Boolean, reflect: true },
  };

  static styles = [style, globalStyle];

  connectedCallback() {
    super.connectedCallback();
    this.coAppPrefix = this.applicantType === 'coApplicant' ? 'co-' : '';
    if (ApplicationService.isIconicV2) {
      // Iconic v2 component already appends the prefix
      this.coAppPrefix = '';
    }
    if (ApplicationService.isIconicV2 && this.applicantType === 'coApplicant') {
      // ID for DOM elements
      this.firstNameFieldId = 'co-firstName';
      this.lastNameFieldId = 'co-lastName';
    } else {
      this.firstNameFieldId = `${this.coAppPrefix}firstName`;
      this.lastNameFieldId = `${this.coAppPrefix}lastName`;
    }

    this.firstNameId = `${this.coAppPrefix}firstName`;
    this.lastNameId = `${this.coAppPrefix}lastName`;
    this.application = ApplicationService.Application;
    this.values = {
      [`${this.coAppPrefix}firstName`]:
        this.application[this.applicantType].firstName,
      [`${this.coAppPrefix}lastName`]:
        this.application[this.applicantType].lastName,
    };
  }

  resetValues() {
    this.application = ApplicationService.Application;
    this.values = {
      [`${this.coAppPrefix}firstName`]:
        this.application[this.applicantType].firstName,
      [`${this.coAppPrefix}lastName`]:
        this.application[this.applicantType].lastName,
    };

    // Ensure DOM is settled before grabbing the element
    setTimeout(() => {
      const firstNameField = this.shadowRoot.getElementById(
        this.firstNameFieldId
      );
      const lastNameField = this.shadowRoot.getElementById(
        this.lastNameFieldId
      );
      firstNameField.setAttribute(
        'value',
        this.values[`${this.coAppPrefix}firstName`]
      );
      lastNameField.setAttribute(
        'value',
        this.values[`${this.coAppPrefix}lastName`]
      );
      if (firstNameField.value !== '') {
        this.values.firstName = firstNameField.value;
      }
      if (lastNameField.value !== '') {
        this.values.lastName = lastNameField.value;
      }
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: this.firstNameId,
            isValid:
              firstNameField.validity.valid && firstNameField.value !== '',
          },
        })
      );
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: this.lastNameId,
            isValid: lastNameField.validity.valid && lastNameField.value !== '',
          },
        })
      );
      this.requestUpdate();
    }, 0);
  }

  firstUpdated() {
    this.resetValues();
  }

  render() {
    return html`
      <div class="${this.compact ? 'personal__compact-name' : ''}">
        <div class="personal__first-name">
          <fs-text-field
            id="${this.firstNameFieldId}"
            class="${this.iconicV2 ? 'iconic-v2' : ''}"
            autocomplete="given-name"
            name="personalName.firstName"
            type="text"
            value-missing-message="Please enter your first name"
            maxLength="32"
            pattern="(([ \\-]*)?[A-Za-z]+([ \\-]*)?)*"
            required
            @change="${(ev) => this.changed('firstName', ev)}"
            @blur="${(ev) => {
              if (ev.target.validity.valid) {
                stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                  replacements: { value: this.firstNameId },
                });
              } else {
                stratum.publishTag(
                  TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                  {
                    replacements: {
                      value: 'Please enter your legal first name',
                    },
                  }
                );
              }
            }}"
            value="${this.values[`${this.coAppPrefix}firstName`]}"
            pattern-mismatch-message="Please enter your legal first name"
            autofocus
            data-cy="${this.firstNameFieldId ?? `${this.coAppPrefix}firstName`}"
          >
            <slot name="firstName">First name</slot>
          </fs-text-field>
        </div>

        <div class="personal__last-name">
          <fs-text-field
            id="${this.lastNameFieldId}"
            class="${this.iconicV2 ? 'iconic-v2' : ''}"
            autocomplete="family-name"
            name="personalName.lastName"
            type="text"
            value-missing-message="Please enter your last name"
            maxLength="32"
            minLength="2"
            too-short-message="Your last name must include more than one letter"
            pattern="(([ \\-]*)?[A-Za-z]+([ \\-]*)?[A-Za-z]+([ \\-]*)?)*"
            required
            @change="${(ev) => this.changed('lastName', ev)}"
            @blur="${(ev) => {
              if (ev.target.validity.valid) {
                stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                  replacements: { value: this.lastNameId },
                });
              } else {
                stratum.publishTag(
                  TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                  {
                    replacements: {
                      value:
                        ev.target.value.length > 1
                          ? 'Please enter your legal first name'
                          : 'Your last name must include more than one letter',
                    },
                  }
                );
              }
            }}"
            value="${this.values[`${this.coAppPrefix}lastName`]}"
            pattern-mismatch-message="Please enter your legal last name"
            data-cy="${this.lastNameFieldId ?? `${this.coAppPrefix}lastName`}"
          >
            <slot name="lastName">Last name</slot>
          </fs-text-field>
        </div>
      </div>
    `;
  }

  changed(name, ev) {
    if (ev.target.validity.valid) {
      this.values[`${this.coAppPrefix}${name}`] = ev.target.value;
    }
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name: this.coAppPrefix + name,
          isValid: ev.target.validity.valid,
        },
      })
    );
  }
}

customElements.define('refi-personal-name', RefiPersonalNameComponent);
